/**
 * Navigation en deux espaces (révision du référent du 2026-10-31, docs/ux/navigation.md) :
 * logique pure, testée à part (apps/web/tests/unit/navigation*.test.ts).
 *
 * - **Espace bénévole** = l'application : Accueil · Dispos · Activités · Live · Plus (Mon
 *   compte dans Plus et derrière l'avatar).
 * - **Espace gestion** = l'intranet et le bloc « Gestion » de l'application : Opérations,
 *   Logistique, Bénévoles, Communication, Commandement, Support ; accueil orienté tâches.
 * - **Administration** à part, derrière l'engrenage.
 *
 * Le serveur calcule, selon les droits, les écrans, actions et tuiles permis et l'espace de
 * chacun (`espace`, déduit de sa rubrique) ; l'interface ne fait que les ranger par rubrique,
 * bloc, carte et ordre : elle n'ajoute jamais d'écran (seule la carte « Réglages des
 * modules », sommaire des écrans de réglages déjà permis, est composée ici) et ne décide
 * d'aucun droit.
 */

export type Espace = 'benevole' | 'gestion' | 'administration';

export type Domaine =
  | 'dispos'
  | 'activites'
  | 'live'
  | 'plus'
  | 'compte'
  | 'operations'
  | 'logistique'
  | 'benevoles'
  | 'communication'
  | 'commandement'
  | 'support'
  | 'administration';

export interface NavEntry {
  section: string;
  label: string;
  path: string;
  icon?: string | undefined;
  /** Entrée regroupée dont cet écran est un onglet (module.json `group`). */
  group?: string | undefined;
  description?: string | undefined;
  module?: string | undefined;
  available?: boolean | undefined;
  /** Espace calculé par le serveur (déduit de la rubrique). */
  espace?: Espace | undefined;
  domaine?: string | undefined;
  bloc?: string | undefined;
  carte?: string | undefined;
  /** Rang de la carte dans sa rubrique (1 = en tête). */
  ordre?: number | undefined;
  /** Page d'entrée de sa rubrique (module.json `entree`, rang de préférence). */
  entree?: number | undefined;
}

/** Carte d'une page de rubrique : un écran, ou plusieurs écrans en onglets. */
export interface NavItem extends NavEntry {
  tabs: NavEntry[];
}

export interface ShellAction {
  key: string;
  label: string;
  path: string;
  icon?: string | undefined;
  domaine: string;
  module?: string | undefined;
  accueil?: number | undefined;
  danger?: boolean | undefined;
  gestion?: boolean | undefined;
  espace?: Espace | undefined;
}

export interface ShellTile {
  key: string;
  label: string;
  module: string;
  value?: unknown;
  severity?: 'ok' | 'warning' | 'critical' | undefined;
  link?: string | undefined;
  domaine?: string | undefined;
  personnelle?: boolean | undefined;
  espace?: Espace | undefined;
}

export interface DomaineInfo {
  key: Domaine;
  label: string;
  /** Adresse de la rubrique (page de rubrique, ou premier écran pour Dispos, Activités, Live). */
  path: string;
  icon: string;
  phrase: string;
  espace: Espace;
}

/** Rubriques de l'espace bénévole (les entrées de l'application), dans l'ordre. */
export const BENEVOLE: readonly DomaineInfo[] = [
  {
    key: 'dispos',
    label: 'Dispos',
    path: '/engagement/mes-disponibilites',
    icon: 'calendar-check',
    phrase: 'Mes disponibilités.',
    espace: 'benevole',
  },
  {
    key: 'activites',
    label: 'Activités',
    path: '/engagement/mes-activites',
    icon: 'calendar-range',
    phrase: 'Mes activités, mon repas et, si je suis responsable, la vue du responsable.',
    espace: 'benevole',
  },
  {
    key: 'live',
    label: 'Live',
    path: '/engagement/directs',
    icon: 'radio',
    phrase: 'Les directs de l’association.',
    espace: 'benevole',
  },
  {
    key: 'plus',
    label: 'Plus',
    path: '/plus',
    icon: 'menu',
    phrase: 'Boutique, engagement, sondages, photos, matériel, liens utiles et Mon compte.',
    espace: 'benevole',
  },
];

export const COMPTE: DomaineInfo = {
  key: 'compte',
  label: 'Mon compte',
  path: '/mon-compte',
  icon: 'id-card',
  phrase: "Ma fiche, mes échéances, mes notifications, Oasis'Call et mes préférences.",
  espace: 'benevole',
};

/** Rubriques de l'espace gestion (l'intranet et le bloc « Gestion » de l'app), dans l'ordre. */
export const GESTION: readonly DomaineInfo[] = [
  {
    key: 'operations',
    label: 'Opérations',
    path: '/gestion/operations',
    icon: 'clipboard-list',
    phrase:
      'Planification opérationnelle et relances de lecture, repas, BRQ et comptes rendus, disponibilités des IS.',
    espace: 'gestion',
  },
  {
    key: 'logistique',
    label: 'Logistique',
    path: '/gestion/logistique',
    icon: 'package',
    phrase: 'Lots, stocks, pharmacie, emprunts, réservations et flotte.',
    espace: 'gestion',
  },
  {
    key: 'benevoles',
    label: 'Bénévoles',
    path: '/gestion/benevoles',
    icon: 'users',
    phrase:
      'Vie interne et candidats : personnes, sondages, échéances, recrutement, accès, reconnaissance et boutique.',
    espace: 'gestion',
  },
  {
    key: 'communication',
    label: 'Communication',
    path: '/gestion/communication',
    icon: 'megaphone',
    phrase: 'Médias, affichage, diffusion d’urgence et QR codes.',
    espace: 'gestion',
  },
  {
    key: 'commandement',
    label: 'Commandement',
    path: '/gestion/commandement',
    icon: 'siren',
    phrase:
      'Pilotage de crise et déclenchement, recensements, notifier les effectifs, veille, partenaires et organismes, appels.',
    espace: 'gestion',
  },
  {
    key: 'support',
    label: 'Support',
    path: '/gestion/support',
    icon: 'inbox',
    phrase: 'Toutes les demandes, leurs indicateurs et les réponses types.',
    espace: 'gestion',
  },
];

export const ADMINISTRATION: DomaineInfo = {
  key: 'administration',
  label: 'Administration',
  path: '/administration',
  icon: 'settings',
  phrase: 'État de la plateforme, droits, données et réglages.',
  espace: 'administration',
};

/** Accueils des deux espaces. */
export const ACCUEIL_BENEVOLE = '/';
export const ACCUEIL_GESTION = '/gestion';

const ALL: readonly DomaineInfo[] = [...BENEVOLE, COMPTE, ...GESTION, ADMINISTRATION];

/** Rubriques qui ont une page de cartes (les autres mènent directement à leur écran). */
const HUBS: ReadonlySet<Domaine> = new Set<Domaine>([
  'plus',
  'compte',
  'administration',
  ...GESTION.map((g) => g.key),
]);

export function domaineInfo(key: string | undefined): DomaineInfo | undefined {
  return ALL.find((d) => d.key === key);
}

/** Page de rubrique à cette adresse exacte (Plus, Mon compte, rubriques de gestion, Administration). */
export function hubAt(pathname: string): DomaineInfo | undefined {
  return ALL.find((d) => HUBS.has(d.key) && d.path === pathname);
}

/** Titres des blocs d'une page de rubrique. */
export const BLOCS: Record<string, string> = {
  moi: 'Pour moi',
  boutique: 'Boutique',
  engagement: 'Mon engagement',
  participer: 'Participer',
  materiel: 'Matériel',
  gerer: 'Gérer',
  piloter: 'Piloter',
  plateforme: 'Plateforme',
  droits: 'Droits et données',
  reglages: 'Réglages',
};
const BLOC_ORDER = Object.keys(BLOCS);

/** Section des réglages des modules (catalogue → page du module à onglets). */
export const SETTINGS_SECTION = 'Administration › Réglages des modules';
export const SETTINGS_PATH = '/administration/reglages';
export const SETTINGS_LABEL = 'Réglages des modules';

/** Rubrique d'un écran ; un écran sans rubrique déclarée (phase 0) va à l'administration. */
export const domaineOf = (e: NavEntry): Domaine => domaineInfo(e.domaine)?.key ?? 'administration';

/** Espace d'un écran : celui calculé par le serveur, sinon celui de sa rubrique. */
export const espaceOf = (e: NavEntry): Espace =>
  e.espace ?? domaineInfo(domaineOf(e))?.espace ?? 'administration';

const blocOf = (e: NavEntry): string =>
  e.bloc ??
  (domaineOf(e) === 'administration' ? 'plateforme' : espaceOf(e) === 'benevole' ? 'moi' : 'gerer');
const carteOf = (e: NavEntry): string => e.carte ?? e.group ?? e.label;
const isSettings = (e: NavEntry): boolean => e.section === SETTINGS_SECTION;

/** Écrans disponibles (module actif), rangés par `ordre` (stable : l'ordre des modules sinon). */
const usable = (entries: readonly NavEntry[]): NavEntry[] =>
  entries
    .filter((e) => e.available !== false)
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.ordre ?? 99) - (b.e.ordre ?? 99) || a.i - b.i)
    .map(({ e }) => e);

/** Regroupe des écrans en cartes (une par nom de carte), dans l'ordre reçu, sans doublon. */
function cardsOf(list: readonly NavEntry[], name: (e: NavEntry) => string = carteOf): NavItem[] {
  const cards: NavItem[] = [];
  const byName = new Map<string, NavItem>();
  for (const entry of list) {
    const key = name(entry);
    const existing = byName.get(key);
    if (existing !== undefined) {
      if (!existing.tabs.some((t) => t.path === entry.path)) existing.tabs.push(entry);
      existing.description ??= entry.description;
      continue;
    }
    const card: NavItem = { ...entry, label: key, tabs: [entry] };
    byName.set(key, card);
    cards.push(card);
  }
  return cards;
}

/** Pages de réglages des modules permises (une par module, ses écrans en onglets). */
export function moduleSettings(entries: readonly NavEntry[]): NavItem[] {
  return cardsOf(usable(entries).filter(isSettings), (e) => e.group ?? e.module ?? e.label);
}

const settingsCard = (): NavItem => ({
  section: SETTINGS_SECTION,
  label: SETTINGS_LABEL,
  path: SETTINGS_PATH,
  icon: 'sliders-horizontal',
  description: 'Réglages propres à chaque module, regroupés par module.',
  domaine: 'administration',
  bloc: 'reglages',
  espace: 'administration',
  tabs: [],
});

export interface HubBloc {
  key: string;
  title: string;
  cards: NavItem[];
}

/**
 * Blocs et cartes d'une rubrique : chaque carte n'apparaît que si l'un de ses onglets est
 * permis, un bloc que s'il a une carte ; cartes rangées par `ordre`. L'adresse de la page
 * elle-même n'est pas une carte.
 */
export function hubOf(domaine: Domaine, entries: readonly NavEntry[]): HubBloc[] {
  const hubPath = HUBS.has(domaine) ? domaineInfo(domaine)?.path : undefined;
  const mine = usable(entries).filter(
    (e) => domaineOf(e) === domaine && e.path !== hubPath && !isSettings(e),
  );
  const blocs = new Map<string, NavEntry[]>();
  for (const e of mine) blocs.set(blocOf(e), [...(blocs.get(blocOf(e)) ?? []), e]);
  const out: HubBloc[] = [...blocs.entries()]
    .map(([key, list]) => ({ key, title: BLOCS[key] ?? key, cards: cardsOf(list) }))
    .sort((a, b) => BLOC_ORDER.indexOf(a.key) - BLOC_ORDER.indexOf(b.key));
  if (domaine === 'administration' && moduleSettings(entries).length > 0) {
    const reglages = out.find((b) => b.key === 'reglages');
    if (reglages !== undefined) reglages.cards.unshift(settingsCard());
    else
      out.push({ key: 'reglages', title: BLOCS.reglages ?? 'Réglages', cards: [settingsCard()] });
  }
  return out;
}

/** Tous les écrans d'une rubrique (cartes et onglets). */
export function hubPaths(domaine: Domaine, entries: readonly NavEntry[]): string[] {
  return hubOf(domaine, entries).flatMap((b) =>
    b.cards.flatMap((c) => (c.tabs.length === 0 ? [c.path] : c.tabs.map((t) => t.path))),
  );
}

/** Adresse d'une rubrique : sa page, ou son premier écran permis (Dispos, Activités, Live). */
export function rubriquePath(d: DomaineInfo, entries: readonly NavEntry[]): string {
  if (HUBS.has(d.key)) return d.path;
  return usable(entries).find((e) => domaineOf(e) === d.key)?.path ?? d.path;
}

/**
 * Page d'entrée d'une rubrique de gestion (module.json `entree`, docs/ux/navigation.md § 4) :
 * l'écran permis de plus petit rang. La page de la rubrique s'ouvre alors directement sur lui ;
 * ses cartes restent à `cartesPath`.
 */
export function entreeDe(domaine: Domaine, entries: readonly NavEntry[]): NavEntry | undefined {
  return usable(entries)
    .filter((e) => e.entree !== undefined && domaineOf(e) === domaine && !isSettings(e))
    .sort((a, b) => (a.entree ?? 99) - (b.entree ?? 99))[0];
}

/** Paramètre qui affiche les cartes d'une rubrique qui a une page d'entrée. */
export const VUE_CARTES = 'cartes';

/** Adresse des cartes d'une rubrique (tous ses écrans), même si elle a une page d'entrée. */
export function cartesPath(d: DomaineInfo, entries: readonly NavEntry[]): string {
  return entreeDe(d.key, entries) === undefined
    ? rubriquePath(d, entries)
    : `${d.path}?vue=${VUE_CARTES}`;
}

/** Rubriques de gestion visibles (au moins une carte permise), dans l'ordre fixe. */
export function gestionRubriques(entries: readonly NavEntry[]): DomaineInfo[] {
  return GESTION.filter((d) => hubOf(d.key, entries).length > 0);
}

/** Rubriques de gestion affichées dans l'en-tête au plus ; les autres vont dans « Autres ». */
export const RUBRIQUES_MAX = 4;

/**
 * En-tête de la gestion selon les responsabilités (demande du référent du 2026-11-03) :
 * rubriques épinglées dans Mon compte si la personne en a choisi, sinon les RUBRIQUES_MAX où
 * elle a le plus d'écrans ; toujours dans l'ordre fixe, le reste dans « Autres ». Jusqu'à
 * RUBRIQUES_MAX rubriques permises, toutes restent visibles.
 */
export function rubriquesEnTete(
  rubriques: readonly DomaineInfo[],
  entries: readonly NavEntry[],
  epinglees: readonly string[] | null,
): { visibles: DomaineInfo[]; autres: DomaineInfo[] } {
  if (rubriques.length <= RUBRIQUES_MAX && epinglees === null)
    return { visibles: [...rubriques], autres: [] };
  const choisies =
    epinglees === null
      ? new Set<string>(
          [...rubriques]
            .map((d, rang) => ({ d, rang, n: hubPaths(d.key, entries).length }))
            .sort((a, b) => b.n - a.n || a.rang - b.rang)
            .slice(0, RUBRIQUES_MAX)
            .map((x) => x.d.key),
        )
      : new Set<string>(epinglees.filter((k) => rubriques.some((d) => d.key === k)));
  // Épinglage vide ou obsolète (rubriques retirées) : retour à la règle par défaut.
  if (choisies.size === 0) return rubriquesEnTete(rubriques, entries, null);
  return {
    visibles: rubriques.filter((d) => choisies.has(d.key)),
    autres: rubriques.filter((d) => !choisies.has(d.key)),
  };
}

/**
 * L'espace gestion existe-t-il pour cette personne ? Vrai seulement si le serveur lui a
 * permis au moins un écran de gestion : sinon, pas de bouton pour y passer.
 */
export function hasGestion(entries: readonly NavEntry[]): boolean {
  return gestionRubriques(entries).length > 0;
}

/** L'engrenage n'apparaît qu'avec au moins un écran d'administration. */
export function hasAdministration(entries: readonly NavEntry[]): boolean {
  return hubOf('administration', entries).length > 0;
}

export interface BarItem {
  key: string;
  label: string;
  path: string;
  icon: string;
}

/**
 * Entrées de l'espace bénévole (barre du haut et barre du bas), comme l'app : Accueil · Dispos
 * · Activités · Live · Plus. Une rubrique sans écran permis (module inactif) est omise.
 */
export function benevoleBar(entries: readonly NavEntry[]): BarItem[] {
  const items: BarItem[] = [{ key: 'accueil', label: 'Accueil', path: '/', icon: 'house' }];
  for (const d of BENEVOLE) {
    if (d.key !== 'plus' && !usable(entries).some((e) => domaineOf(e) === d.key)) continue;
    items.push({ key: d.key, label: d.label, path: rubriquePath(d, entries), icon: d.icon });
  }
  return items;
}

/** Espace où se trouve l'adresse (la coque en déduit son en-tête). */
export function espaceAt(entries: readonly NavEntry[], pathname: string): Espace {
  if (pathname === ACCUEIL_GESTION || pathname.startsWith(`${ACCUEIL_GESTION}/`)) return 'gestion';
  if (pathname === ADMINISTRATION.path || pathname.startsWith(`${ADMINISTRATION.path}/`))
    return 'administration';
  const here = locate(entries, pathname);
  return here === undefined ? 'benevole' : espaceOf(here.tab);
}

/**
 * En-tête affiché pour un espace : l'administration (à part) garde l'en-tête de gestion pour
 * qui a la gestion, sinon celui du bénévole.
 */
export function chromeOf(espace: Espace, entries: readonly NavEntry[]): 'benevole' | 'gestion' {
  if (espace === 'administration') return hasGestion(entries) ? 'gestion' : 'benevole';
  return espace;
}

/**
 * Barre du bas sur téléphone (5 entrées au plus). Espace bénévole : les entrées de l'app.
 * Espace gestion : accueil de la gestion, ses trois premières rubriques, « Plus » (les autres
 * rubriques et l'administration).
 */
export function bottomBar(entries: readonly NavEntry[], chrome: 'benevole' | 'gestion'): BarItem[] {
  if (chrome === 'benevole') return benevoleBar(entries);
  const items: BarItem[] = [
    { key: 'gestion', label: 'Accueil', path: ACCUEIL_GESTION, icon: 'layout-dashboard' },
    ...gestionRubriques(entries)
      .slice(0, 3)
      .map((d) => ({ key: d.key, label: d.label, path: d.path, icon: d.icon })),
  ];
  return [...items, { key: 'plus', label: 'Plus', path: '', icon: 'menu' }];
}

/** Espace d'une action ou d'une tuile (calculé par le serveur ; à défaut, ses vues). */
const actionEspace = (a: ShellAction): Espace =>
  a.espace ?? domaineInfo(a.domaine)?.espace ?? (a.gestion === true ? 'gestion' : 'benevole');
const tileEspace = (t: ShellTile): Espace =>
  t.espace ?? domaineInfo(t.domaine)?.espace ?? (t.personnelle === true ? 'benevole' : 'gestion');

const rank = (a: ShellAction): number => a.accueil ?? 99;
const byRank = (list: readonly ShellAction[]): ShellAction[] =>
  list
    .map((a, i) => ({ a, i }))
    .sort((x, y) => rank(x.a) - rank(y.a) || x.i - y.i)
    .map(({ a }) => a);

/**
 * Boutons d'une page de rubrique : 3 au plus, par rang d'accueil (le premier est le bouton
 * principal), l'urgence (danger) en dernier.
 */
export function domainActions(domaine: Domaine, actions: readonly ShellAction[]): ShellAction[] {
  const mine = actions.filter((a) => a.domaine === domaine);
  const danger = mine.filter((a) => a.danger === true).slice(0, 1);
  const rest = byRank(mine.filter((a) => a.danger !== true));
  return [...rest.slice(0, 3 - danger.length), ...danger];
}

export const HOME_ACTIONS_MAX = 8;

/**
 * Boutons de l'accueil d'un espace (≤ 8), seulement ceux de cet espace. Bénévole : par rang.
 * Gestion : une action par rubrique à tour de rôle, dans l'ordre des rubriques (Opérations
 * d'abord : « Importer l'export e-Protec »), la meilleure de chacune d'abord.
 */
export function homeActions(actions: readonly ShellAction[], espace: Espace): ShellAction[] {
  const listed = actions.filter((a) => a.accueil !== undefined && actionEspace(a) === espace);
  if (espace !== 'gestion') return byRank(listed).slice(0, HOME_ACTIONS_MAX);
  const order: string[] = GESTION.map((g) => g.key);
  const byDomain = new Map<string, ShellAction[]>();
  for (const a of byRank(listed)) byDomain.set(a.domaine, [...(byDomain.get(a.domaine) ?? []), a]);
  const domains = [...byDomain.keys()].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const out: ShellAction[] = [];
  for (let round = 0; out.length < HOME_ACTIONS_MAX; round += 1) {
    const picked = domains.flatMap((d) => byDomain.get(d)?.[round] ?? []);
    if (picked.length === 0) break;
    out.push(...picked.slice(0, HOME_ACTIONS_MAX - out.length));
  }
  return out;
}

/** Valeurs qui ne disent rien à faire : jamais affichées en compteur (audit rc.5). */
const EMPTY_VALUES = new Set(['', '0', '—', '-', '–', 'n/a']);

/**
 * Une tuile demande-t-elle une action (compteur non nul en alerte) ? Une tuile sans gravité
 * est une information (effectif, disponibles maintenant, badges récents) : jamais « à
 * traiter » (sinon l'effectif s'ajoutait au compteur de la rubrique, retour rc.12).
 */
export function actionable(t: ShellTile): boolean {
  if (t.severity === 'ok' || t.severity === undefined) return false;
  if (typeof t.value === 'number') return t.value > 0;
  if (typeof t.value === 'string') return !EMPTY_VALUES.has(t.value.trim().toLowerCase());
  return false;
}

const SEVERITY_RANK = (s: ShellTile['severity']): number =>
  s === 'critical' ? 0 : s === 'warning' ? 1 : 2;

/** Ligne « À traiter » d'une page de rubrique. */
export function domainTodo(domaine: Domaine, tiles: readonly ShellTile[]): ShellTile[] {
  return tiles
    .filter((t) => t.domaine === domaine && actionable(t))
    .sort((a, b) => SEVERITY_RANK(a.severity) - SEVERITY_RANK(b.severity));
}

/**
 * Tuiles de décision du responsable (« À décider ») mises en tête de l'accueil de gestion. Les
 * tuiles des dossiers d'Opérations (décisions, jalons, factures) sont retirées avec eux
 * (révision du référent du 2026-10-31 : outil national).
 */
export const DECISIONS = new Set(['ct-30-jours']);

export const HOME_TODO_MAX = 6;

/**
 * « À faire » de l'accueil d'un espace (≤ 6), seulement les compteurs de cet espace :
 * décisions d'abord (gestion), puis dans l'ordre des rubriques, par gravité.
 */
export function homeTodo(tiles: readonly ShellTile[], espace: Espace): ShellTile[] {
  const order: string[] = [...BENEVOLE, COMPTE, ...GESTION].map((d) => d.key);
  const dom = (t: ShellTile): number => {
    const i = order.indexOf(t.domaine ?? '');
    return i < 0 ? order.length : i;
  };
  const group = (t: ShellTile): number => (DECISIONS.has(t.key) ? 0 : 1);
  return tiles
    .filter((t) => actionable(t) && tileEspace(t) === espace)
    .sort(
      (a, b) =>
        group(a) - group(b) ||
        dom(a) - dom(b) ||
        SEVERITY_RANK(a.severity) - SEVERITY_RANK(b.severity),
    )
    .slice(0, HOME_TODO_MAX);
}

/** Nombre de choses « à traiter » d'une rubrique (accueil de la gestion). */
export function todoCount(domaine: Domaine, tiles: readonly ShellTile[]): number {
  return domainTodo(domaine, tiles).reduce(
    (n, t) => n + (typeof t.value === 'number' ? t.value : 1),
    0,
  );
}

/**
 * Anciennes pages de domaine (0.4.0-rc.6) : leur adresse reste valide et mène à la rubrique
 * qui les remplace (gestion si la personne y a des écrans, sinon l'espace bénévole).
 */
const ANCIENS_HUBS: Record<string, { gestion?: Domaine; sinon: string }> = {
  '/activites': { gestion: 'operations', sinon: '/engagement/mes-activites' },
  '/benevoles': { gestion: 'benevoles', sinon: '/plus' },
  '/materiel': { gestion: 'logistique', sinon: '/plus' },
  '/demandes': { gestion: 'support', sinon: '/plus' },
  '/communication': { gestion: 'communication', sinon: '/plus' },
  '/vie-associative': { sinon: '/plus' },
  '/crise': { gestion: 'commandement', sinon: '/' },
};
export const ANCIENNES_PAGES = Object.keys(ANCIENS_HUBS);

export function ancienHub(pathname: string, entries: readonly NavEntry[]): string | undefined {
  const ancien = ANCIENS_HUBS[pathname];
  if (ancien === undefined) return undefined;
  const g = ancien.gestion === undefined ? undefined : domaineInfo(ancien.gestion);
  return g !== undefined && hubOf(g.key, entries).length > 0 ? g.path : ancien.sinon;
}

export interface Crumb {
  label: string;
  /** Absent : élément courant (dernier du fil). */
  to?: string | undefined;
}

/** Où suis-je : rubrique, carte, onglet, fil d'Ariane. */
export interface Location {
  /** Titre de la rubrique (surtitre des pages). */
  section: string;
  domaine: DomaineInfo;
  item: NavItem;
  tab: NavEntry;
  /** L'adresse est celle de l'onglet (et non une page de détail). */
  exact: boolean;
  crumbs: Crumb[];
}

/**
 * Écran courant : écran au chemin le plus long qui préfixe l'adresse (pages de détail
 * comprises). Un chemin déclaré pour soi et pour la gestion est situé dans la gestion. Le fil
 * d'Ariane donne Rubrique › Carte (si plusieurs onglets) › Onglet ; pour un réglage de module :
 * Administration › Réglages des modules › Module › Onglet.
 */
export function locate(entries: readonly NavEntry[], pathname: string): Location | undefined {
  const all = usable(entries);
  let best: NavEntry | undefined;
  // Le catalogue des réglages n'est pas une page de détail de « /administration ».
  const scan = pathname === SETTINGS_PATH ? [] : all;
  for (const e of scan) {
    const match = pathname === e.path || (e.path !== '/' && pathname.startsWith(`${e.path}/`));
    if (!match) continue;
    if (
      best === undefined ||
      e.path.length > best.path.length ||
      (e.path === best.path && espaceOf(best) === 'benevole' && espaceOf(e) !== 'benevole')
    )
      best = e;
  }
  if (best === undefined) {
    if (pathname !== SETTINGS_PATH || moduleSettings(entries).length === 0) return undefined;
    const item = settingsCard();
    return {
      section: ADMINISTRATION.label,
      domaine: ADMINISTRATION,
      item,
      tab: item,
      exact: true,
      crumbs: [{ label: ADMINISTRATION.label, to: ADMINISTRATION.path }, { label: SETTINGS_LABEL }],
    };
  }
  const tab = best;
  const settings = isSettings(tab);
  const domaine = domaineInfo(domaineOf(tab)) ?? ADMINISTRATION;
  const item = settings
    ? (moduleSettings(entries).find((m) => m.tabs.some((t) => t.path === tab.path)) ?? {
        ...tab,
        tabs: [tab],
      })
    : (cardsOf(all.filter((e) => domaineOf(e) === domaine.key && blocOf(e) === blocOf(tab))).find(
        (c) => c.tabs.some((t) => t.path === tab.path),
      ) ?? { ...tab, tabs: [tab] });
  const exact = pathname === tab.path;
  // Rubrique à page d'entrée : le fil mène à ses cartes (tous ses écrans), pas à l'entrée.
  const crumbs: Crumb[] = [{ label: domaine.label, to: cartesPath(domaine, entries) }];
  if (settings) crumbs.push({ label: SETTINGS_LABEL, to: SETTINGS_PATH });
  if (item.tabs.length > 1 || settings) crumbs.push({ label: item.label, to: item.path });
  const previous = crumbs.at(-1);
  // Même écran que l'élément précédent (même libellé, ou rubrique qui mène directement à cet
  // écran : Dispos, Live) : il n'est pas répété.
  const same = previous?.to === tab.path && (previous.label === tab.label || crumbs.length === 1);
  if (previous !== undefined && same) {
    if (exact) previous.to = undefined;
  } else crumbs.push({ label: tab.label, to: exact ? undefined : tab.path });
  return { section: domaine.label, domaine, item, tab, exact, crumbs };
}

/** Rubrique de l'écran courant (surtitre des pages). */
export function sectionOfPath(entries: readonly NavEntry[], pathname: string): string | undefined {
  return locate(entries, pathname)?.section;
}
